import { Fragment, createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { ApiError } from "../lib/api"
import { COLOR_PILL, statusOf, useBoardConfig } from "../lib/boardConfig"

// The pieces every page reaches for. Kept in one file because there are only a
// dozen of them and a file each would be more ceremony than the app is worth.

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger" | "subtle"
  size?: "sm" | "md"
}) {
  const styles = {
    primary: "bg-accent text-accent-foreground hover:bg-accent/90",
    ghost: "border border-border bg-surface text-foreground hover:bg-surface-muted",
    danger: "border border-danger/30 bg-danger-soft text-danger hover:bg-danger/10",
    // No border and no ground until hovered. For the row of actions on a
    // toolbar, where four outlined buttons in a line read as a fence.
    subtle: "text-muted hover:bg-surface-muted hover:text-foreground",
  }[variant]

  const sizing = size === "sm" ? "gap-1.5 px-2.5 py-1.5 text-xs" : "gap-2 px-3.5 py-2 text-sm"

  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-interactive ${sizing} ${styles} ${className}`}
    />
  )
}

export function Field({
  label,
  hint,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  const id = props.id ?? props.name
  return (
    <label className="flex flex-col gap-1.5" htmlFor={id}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      <input
        {...props}
        id={id}
        className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none placeholder:text-faint focus:border-interactive focus:ring-2 focus:ring-interactive/30"
      />
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  )
}

export function TextArea({
  label,
  hint,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  const id = props.id ?? props.name
  return (
    <label className="flex flex-col gap-1.5" htmlFor={id}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      <textarea
        {...props}
        id={id}
        className="min-h-24 rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none placeholder:text-faint focus:border-interactive focus:ring-2 focus:ring-interactive/30"
      />
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  )
}

export function Select({
  label,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  const id = props.id ?? props.name
  return (
    <label className="flex flex-col gap-1.5" htmlFor={id}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      <select
        {...props}
        id={id}
        className="rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-interactive focus:ring-2 focus:ring-interactive/30"
      >
        {children}
      </select>
    </label>
  )
}

export function Card({ className = "", ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...props} className={`rounded-lg border border-border bg-surface ${className}`} />
}

export function PageHeader({
  title,
  subtitle,
  action,
  eyebrow,
}: {
  title: string
  subtitle?: string
  action?: React.ReactNode
  /** Small identifier above the title — a task key, for instance. */
  eyebrow?: string
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
      <div className="flex flex-col gap-1">
        {eyebrow ? <span className="font-mono text-xs text-faint">{eyebrow}</span> : null}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="max-w-prose text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </header>
  )
}

// The shipped defaults, kept for the handful of places that render a status
// with no project in scope. Anything inside a project reads the board config.
export const STATUS_LABELS: Record<string, string> = {
  todo: "To do",
  in_progress: "In progress",
  in_review: "In review",
  blocked: "Blocked",
  done: "Done",
}

export const TASK_STATUSES = Object.keys(STATUS_LABELS)

export function StatusPill({ status }: { status: string }) {
  const config = useBoardConfig()
  const meta = statusOf(config, status)
  const label = meta.name
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded border px-2 py-0.5 text-xs font-medium ${COLOR_PILL[meta.color] ?? COLOR_PILL.gray}`}
    >
      {label}
    </span>
  )
}

// Errors say what went wrong and, where the API told us, how to fix it. A bare
// "something went wrong" leaves the user with nothing to do next.
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const message =
    error instanceof ApiError || error instanceof Error ? error.message : "Something went wrong."
  return (
    <div role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
      {message}
    </div>
  )
}

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border px-6 py-14 text-center">
      <p className="font-medium text-foreground">{title}</p>
      {hint && <p className="max-w-sm text-sm text-muted">{hint}</p>}
      {action}
    </div>
  )
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex justify-center py-10">
      <div
        className="size-5 animate-spin rounded-full border-2 border-border border-t-accent"
        role="status"
        aria-label={label}
      />
    </div>
  )
}

// Modest data-fetching hook. Enough for a CRUD app; anything needing caching or
// revalidation across pages would want a real library, and none of these pages
// do yet.
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let live = true
    setLoading(true)
    load()
      .then((value) => live && (setData(value), setError(null)))
      .catch((err) => live && setError(err))
      .finally(() => live && setLoading(false))
    // Guards against a response from a previous page landing after this one.
    return () => {
      live = false
    }
    // eslint-disable-next-border react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { data, error, loading, reload: () => setNonce((n) => n + 1) }
}

export function formatDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

export function formatDateTime(value: string) {
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// ============================================
// The board's vocabulary
// ============================================
//
// Added for the projects area, but deliberately in this shared file: an avatar
// and a dropdown are not board concepts, and a second components file would
// have every page importing from two places to build one row.

export function Badge({
  children,
  tone = "neutral",
  className = "",
  title,
}: {
  children: React.ReactNode
  tone?: "neutral" | "accent" | "success" | "warning" | "danger"
  className?: string
  title?: string
}) {
  const tones = {
    neutral: "border-border bg-surface-muted text-muted",
    accent: "border-accent/30 bg-accent-soft text-accent",
    success: "border-success/30 bg-success-soft text-success",
    warning: "border-warning/30 bg-warning-soft text-warning",
    danger: "border-danger/30 bg-danger-soft text-danger",
  }[tone]

  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-xs ${tones} ${className}`}
    >
      {children}
    </span>
  )
}

// Initials on a tinted ground. The tint is derived from the id, so the same
// person is the same colour everywhere without anyone storing a preference —
// and the hues are fixed rather than random so two colleagues do not collide
// differently on every reload.
const AVATAR_TINTS = [
  "bg-accent-soft text-accent",
  "bg-success-soft text-success",
  "bg-warning-soft text-warning",
  "bg-danger-soft text-danger",
  "bg-surface-muted text-muted",
] as const

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return "?"
  const first = parts[0]![0] ?? ""
  const last = parts.length > 1 ? (parts.at(-1)![0] ?? "") : ""
  return (first + last).toUpperCase()
}

export function Avatar({
  name,
  id,
  size = "sm",
}: {
  name: string | null | undefined
  /** Anything stable per person. Only used to pick the tint. */
  id?: string | null
  size?: "xs" | "sm" | "md"
}) {
  const label = name?.trim() || "Unassigned"
  const seed = (id ?? label).split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
  const tint = name ? AVATAR_TINTS[seed % AVATAR_TINTS.length] : "bg-surface-muted text-faint"
  const dimensions = { xs: "size-5 text-[9px]", sm: "size-6 text-[10px]", md: "size-8 text-xs" }[size]

  return (
    <span
      title={label}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-medium ${dimensions} ${tint}`}
    >
      {name ? initialsOf(name) : "–"}
      <span className="sr-only">{label}</span>
    </span>
  )
}

export function IconButton({
  label,
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...props}
      aria-label={label}
      title={label}
      className={`inline-flex size-7 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-foreground disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-interactive ${className}`}
    />
  )
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-surface-muted ${className}`} aria-hidden="true" />
}

/** Done out of total — children unless told otherwise. Renders nothing at all when there are none —
 *  an empty bar reads as "no progress" rather than "nothing to do". */
export function Progress({
  done,
  total,
  label = "children",
}: {
  done: number
  total: number
  /** What is being counted, for the tooltip. */
  label?: string
}) {
  if (!total) return null
  const pct = Math.round((done / total) * 100)
  return (
    <span className="inline-flex items-center gap-1.5" title={`${done} of ${total} ${label} done`}>
      <span className="h-1 w-8 overflow-hidden rounded-full bg-surface-muted">
        <span
          className={`block h-full rounded-full ${done === total ? "bg-success" : "bg-muted"}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="font-mono text-[10px] text-faint tabular-nums">
        {done}/{total}
      </span>
    </span>
  )
}

// ---------- dates and durations ----------

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** "3m ago", "in 2d". Falls back to a date past a fortnight, where a relative
 *  figure stops being easier to read than the day itself. */
export function relativeTime(value: string | Date) {
  const then = new Date(value).getTime()
  const delta = then - Date.now()
  const ahead = delta > 0
  const size = Math.abs(delta)

  if (size > 14 * DAY) return formatDate(new Date(then).toISOString())

  const [amount, unit] =
    size < HOUR
      ? [Math.max(1, Math.round(size / MINUTE)), "m"]
      : size < DAY
        ? [Math.round(size / HOUR), "h"]
        : [Math.round(size / DAY), "d"]

  return ahead ? `in ${amount}${unit}` : `${amount}${unit} ago`
}

export function RelativeTime({ value, className = "" }: { value: string; className?: string }) {
  return (
    <time dateTime={value} title={formatDateTime(value)} className={className}>
      {relativeTime(value)}
    </time>
  )
}

// Duration parsing and formatting live in lib/duration.ts, where they are
// tested; re-exported so every page keeps importing them from here.
export { describeSpentOfEstimate, formatDuration, formatSpentOfEstimate, parseDuration } from "../lib/duration"

/** An <input type="date"> wants "2026-08-24"; the API speaks ISO instants. */
export const toDateInput = (value: string | null) => (value ? value.slice(0, 10) : "")
export const fromDateInput = (value: string) =>
  value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null

export function isOverdue(dueDate: string | null, status: string) {
  if (!dueDate || status === "done") return false
  return new Date(dueDate).getTime() < Date.now()
}

// ---------- overlays ----------

/** Closes on outside click and on Escape, and restores focus to the trigger.
 *  Used by both the dropdown and the modal, because getting one of the three
 *  right and not the others is what makes a menu feel broken. */
//
// Escape closes only the topmost open layer: a dropdown inside a modal closes
// first, and the modal on the next Escape. Every open layer listens on the
// document, so without this stack one keypress closed both.
const dismissStack: object[] = []

function useDismiss(open: boolean, close: () => void, ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return
    const layer = {}
    dismissStack.push(layer)
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissStack.at(-1) === layer) close()
    }
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close()
    }
    document.addEventListener("keydown", onKey)
    // `capture` so a click on a button that stops propagation still dismisses.
    document.addEventListener("mousedown", onClick, true)
    return () => {
      dismissStack.splice(dismissStack.indexOf(layer), 1)
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onClick, true)
    }
  }, [open, close, ref])
}

export type MenuItem = {
  label: React.ReactNode
  onSelect: () => void
  /** Marks the current value with a tick, for menus that pick rather than act. */
  selected?: boolean
  danger?: boolean
  /** A checkbox-style toggle instead of the tick. Kept out of Dropdown's own
   *  items so an action menu can never grow one by accident. */
  checkbox?: boolean
  /** Shown but not offered — the picker's current value when it can no longer
   *  be chosen. The click does nothing, so onSelect is never reached. */
  disabled?: boolean
  /** A rule above this item, for grouping a menu into sections. */
  dividerBefore?: boolean
}

export function Dropdown({
  trigger,
  items,
  align = "left",
  // Which way the menu opens. "up" exists for triggers that sit at the bottom
  // of the viewport — a chat's status bar, above the composer — where a menu
  // dropped downwards is clipped by the window rather than by any container,
  // so no amount of overflow handling on the parent rescues it.
  drop = "down",
  className = "",
  header,
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => React.ReactNode
  items: MenuItem[]
  align?: "left" | "right"
  drop?: "up" | "down"
  className?: string
  /** Pinned above the items inside the menu — a search box on a long list. */
  header?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(open, close, ref)

  return (
    <div ref={ref} className={`relative ${className}`}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div
          role="menu"
          className={`absolute z-30 max-h-72 min-w-44 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-lg ${
            align === "right" ? "right-0" : "left-0"
          } ${drop === "up" ? "bottom-full mb-1" : "mt-1"}`}
        >
          {header}
          {items.map((item, index) => (
            <Fragment key={index}>
              {item.dividerBefore && <div role="separator" className="my-1 border-t border-border" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (!item.checkbox) setOpen(false)
                  item.onSelect()
                }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
                  item.disabled
                    ? "cursor-not-allowed text-faint"
                    : `hover:bg-surface-muted ${item.danger ? "text-danger" : "text-foreground"}`
                }`}
              >
                {item.checkbox ? (
                  <span
                    className={`flex size-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                      item.selected ? "border-accent bg-accent text-accent-foreground" : "border-border"
                    }`}
                  >
                    {item.selected ? "✓" : ""}
                  </span>
                ) : (
                  <span className="w-3.5 shrink-0 text-accent">{item.selected ? "✓" : ""}</span>
                )}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

/** A multi-value picker built on Dropdown: checkbox items that stay open while
 *  toggling, an optional search box, and a trigger that says how many are
 *  selected. The Time Report's filter bar is the first user; a menu that
 *  closed on every tick would make a three-project filter five clicks. */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  search,
  className = "",
}: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (next: string[]) => void
  search?: boolean
  className?: string
}) {
  const [query, setQuery] = useState("")
  const visible = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  const items: MenuItem[] = visible.map((option) => ({
    label: option.label,
    checkbox: true,
    selected: selected.includes(option.value),
    onSelect: () =>
      onChange(
        selected.includes(option.value)
          ? selected.filter((v) => v !== option.value)
          : [...selected, option.value],
      ),
  }))

  return (
    <Dropdown
      className={className}
      items={
        items.length
          ? items
          : [{ label: search && query ? "No matches" : "Nothing to pick", onSelect: () => {} }]
      }
      header={
        search ? (
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="sticky top-0 w-full border-b border-border bg-surface px-3 py-1.5 text-sm outline-none placeholder:text-faint"
          />
        ) : undefined
      }
      trigger={({ toggle }) => (
        // type="button": a MultiSelect inside a form must open, not submit it.
        <Button type="button" variant="ghost" size="sm" onClick={toggle}>
          {label}
          {selected.length > 0 && (
            <span className="rounded-full bg-accent-soft px-1.5 text-[10px] font-medium text-accent">
              {selected.length}
            </span>
          )}
        </Button>
      )}
    />
  )
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
  /** For forms carrying an editor — a narrow column fights the block editor's
   *  paragraphs and code blocks. */
  wide?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(true, onClose, ref)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background/70 p-4 pt-[10vh] backdrop-blur-sm">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`flex w-full flex-col gap-4 rounded-lg border border-border bg-surface p-5 shadow-xl ${wide ? "max-w-4xl" : "max-w-md"}`}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <IconButton label="Close" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </IconButton>
        </div>
        {children}
        {footer}
      </div>
    </div>
  )
}

// ---------- toasts ----------
//
// A save that changes nothing visible — a date, a label, a status on a page
// that does not re-sort — otherwise looks like a click that did not register.

type Toast = { id: number; message: string; tone: "ok" | "error" }
const ToastContext = createContext<(message: string, tone?: "ok" | "error") => void>(() => {})

export const useToast = () => useContext(ToastContext)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const next = useRef(0)

  const push = useCallback((message: string, tone: "ok" | "error" = "ok") => {
    const id = next.current++
    setToasts((all) => [...all, { id, message, tone }])
    setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), 3200)
  }, [])

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            className={`pointer-events-auto rounded-md border px-3 py-2 text-sm shadow-lg ${
              toast.tone === "error"
                ? "border-danger/30 bg-danger-soft text-danger"
                : "border-border bg-surface text-foreground"
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// ---------- editing in place ----------

/** Click the text, type, Enter to save and Escape to cancel. Blur also saves,
 *  because a click elsewhere is how most people leave a field they meant to
 *  keep — Escape is the explicit way to discard. */
export function InlineEdit({
  value,
  onSave,
  multiline,
  placeholder = "Empty",
  className = "",
  inputClassName = "",
  ariaLabel,
}: {
  value: string
  onSave: (next: string) => Promise<void> | void
  multiline?: boolean
  placeholder?: string
  className?: string
  inputClassName?: string
  ariaLabel: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [busy, setBusy] = useState(false)
  const cancelled = useRef(false)

  // A save elsewhere — or another person's edit arriving on reload — must not
  // be overwritten by a stale draft sitting in state.
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  async function commit() {
    if (cancelled.current) {
      cancelled.current = false
      setDraft(value)
      setEditing(false)
      return
    }
    const trimmed = draft.trim()
    setEditing(false)
    if (trimmed === value.trim()) return
    setBusy(true)
    try {
      await onSave(trimmed)
    } finally {
      setBusy(false)
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label={`${ariaLabel} — click to edit`}
        className={`-mx-1 rounded px-1 text-left transition-colors hover:bg-surface-muted ${busy ? "opacity-50" : ""} ${className}`}
      >
        {value.trim() ? value : <span className="text-faint">{placeholder}</span>}
      </button>
    )
  }

  const shared = {
    autoFocus: true,
    value: draft,
    "aria-label": ariaLabel,
    onBlur: commit,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelled.current = true
        ;(e.target as HTMLElement).blur()
      }
      // In a textarea Enter has to stay a newline, so Cmd/Ctrl+Enter saves.
      if (e.key === "Enter" && (!multiline || e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        ;(e.target as HTMLElement).blur()
      }
    },
    className: `-mx-1 w-full rounded border border-interactive bg-surface px-1 outline-none ${inputClassName}`,
  }

  return multiline ? <textarea {...shared} rows={6} /> : <input {...shared} />
}

/** Board / List, and anything else that is two or three exclusive views. */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T
  options: { value: T; label: React.ReactNode; title?: string }[]
  onChange: (value: T) => void
  ariaLabel: string
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex rounded-md border border-border bg-surface p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.title}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors ${
            value === option.value
              ? "bg-accent-soft text-accent"
              : "text-muted hover:text-foreground"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
